import { extractTemplateVariables } from "../../../components/whatsapp/templates/TemplateUtils";

/**
 * Parse a raw BUTTONS component value (string or object) into a flat array of button objects.
 * Centralises the repeated JSON.parse-or-passthrough + array normalisation pattern.
 */
export const parseTemplateButtons = (buttonsComponent) => {
  if (!buttonsComponent) return [];
  try {
    const parsed = typeof buttonsComponent === "string"
      ? JSON.parse(buttonsComponent)
      : buttonsComponent;
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.buttons)) return parsed.buttons;
    return [];
  } catch {
    return [];
  }
};

/**
 * Parse saved `components[]` array back into `{ header, body, button, code }` params.
 * Used when rehydrating a template node from a previously-saved workflow.
 */
export const parseTemplateParamsFromComponents = (components, template) => {
  const params = {
    header: {},
    body: {},
    button: {},
    code: undefined
  };

  if (!template) return params;

  // Pre-initialize variables with empty strings so inputs are always defined
  const headerVars = extractTemplateVariables(template.headerText);
  const bodyVars = extractTemplateVariables(template.body);
  headerVars.forEach(v => { params.header[v] = ""; });
  bodyVars.forEach(v => { params.body[v] = ""; });

  const btnList = parseTemplateButtons(template.components?.BUTTONS);
  btnList.forEach((btn, buttonIndex) => {
    if (!btn) return;
    const url = btn.url || '';
    const matches = typeof url === 'string' ? (url.match(/\{\{\d+\}\}/g) || []) : [];
    matches.forEach((m, paramIndex) => {
      params.button[`button_${buttonIndex}_${paramIndex}`] = "";
    });
  });

  const isAuth = template.category?.toLowerCase() === 'authentication' && (template.otpType || 'copy_code') === 'copy_code';
  if (isAuth) params.code = "";

  if (!Array.isArray(components)) return params;

  components.forEach((comp) => {
    switch (comp.type) {
      case "header":
        if (Array.isArray(comp.parameters)) {
          comp.parameters.forEach((param) => {
            if (param.parameter_name) {
              params.header[param.parameter_name] = param.text || "";
            }
          });
        }
        break;

      case "body":
        if (Array.isArray(comp.parameters)) {
          comp.parameters.forEach((param) => {
            if (param.parameter_name) {
              params.body[param.parameter_name] = param.text || "";
            }
          });
        }
        break;

      case "button": {
        const btnIndex = parseInt(comp.index || "0");
        if (Array.isArray(comp.parameters)) {
          comp.parameters.forEach((param, paramIndex) => {
            const key = `button_${btnIndex}_${paramIndex}`;
            params.button[key] = param.text || "";
          });
        }
        break;
      }

      default:
        break;
    }
  });

  return params;
};

/**
 * Extract the media link (image/video/document URL) from saved components.
 */
export const parseHeaderMediaLinkFromComponents = (components, headerFormat) => {
  if (!Array.isArray(components) || !headerFormat || headerFormat === "text") return "";
  const headerComp = components.find(c => c.type === "header");
  if (headerComp && Array.isArray(headerComp.parameters) && headerComp.parameters.length > 0) {
    const param = headerComp.parameters[0];
    if (param.type === headerFormat && param[headerFormat]) {
      return param[headerFormat].link || "";
    }
  }
  return "";
};

/**
 * Create an empty params structure `{ header, body, button, code }` from a template definition.
 * Used when a template is freshly selected in the inspector.
 */
export const initializeTemplateParams = (template) => {
  if (!template) return {};
  const headerVars = extractTemplateVariables(template.headerText);
  const bodyVars = extractTemplateVariables(template.body);

  const btnList = parseTemplateButtons(template.components?.BUTTONS);
  const btnVars = [];
  btnList.forEach((btn, buttonIndex) => {
    if (!btn) return;
    const url = btn.url || '';
    const matches = typeof url === 'string' ? (url.match(/\{\{\d+\}\}/g) || []) : [];
    matches.forEach((m, paramIndex) => {
      btnVars.push({ key: `button_${buttonIndex}_${paramIndex}` });
    });
  });

  const isAuth = template.category?.toLowerCase() === 'authentication' && (template.otpType || 'copy_code') === 'copy_code';

  return {
    header: headerVars.reduce((acc, v) => ({ ...acc, [v]: '' }), {}),
    body: bodyVars.reduce((acc, v) => ({ ...acc, [v]: '' }), {}),
    button: btnVars.reduce((acc, v) => ({ ...acc, [v.key]: '' }), {}),
    code: isAuth ? '' : undefined
  };
};
